import React from 'react';
import { Link } from 'react-router-dom';
import { useBranding } from '@/context/BrandingContext';
import DefaultMonogramSVG from '@/components/DefaultMonogramSVG';
import VersionBadge from '@/components/VersionBadge';

/**
 * Vigilant Entities — Brand v2.4 (Sovereign Violet · Monogram + Tenant Branding).
 *
 * The mark is now an inline transparent SVG (no grey halo).  Tenant-
 * uploaded logos still flow through the `logo_url` field and respect
 * `logo_auto_transparent` for white-background knock-out.
 *
 *   <LogoMark />   → just the VE monogram (favicon-ready)
 *   <Logo />       → monogram + LIVE TEXT wordmark for headers / sidebars
 *   <LogoFull />   → big monogram + stacked wordmark for hero splashes
 *
 * Tenant-branding integration:
 *   - If the tenant has uploaded a `logo_url`, we render that instead of
 *     the default VE monogram.
 *   - If `logo_auto_transparent === true`, we wrap the image in a holder
 *     that uses `mix-blend-mode: screen` (dark theme) / `multiply` (light
 *     theme) so any white background on the uploaded asset drops out
 *     automatically — operator never needs to pre-process their PNG.
 *   - The holder optionally renders a subtle animated halo behind the
 *     mark using the tenant's primary colour.
 */

const LOGO_GLOW =
  'drop-shadow(0 6px 20px rgba(139,63,197,0.45)) drop-shadow(0 2px 4px rgba(0,0,0,0.30))';

/* -------------------------------------------------------------------------- */
/*  <LogoMark> — Icon-only.  If the tenant uploaded a `logo_url` we render    */
/*  that as an <img>; otherwise we render the transparent SVG monogram.       */
/* -------------------------------------------------------------------------- */
export function LogoMark({ className = 'h-14 w-14', noLink = false, to = '/home' }) {
  const { branding } = useBranding();
  const isCustom = !!branding?.logo_url;
  const autoTransparent = !!branding?.logo_auto_transparent;
  const primary = branding?.primary_color || '#AE52EA';
  // mix-blend-mode strips a white-background uploaded PNG against dark
  // surfaces; in light theme we use multiply so the dark monogram still
  // shows.  Detection via `html.dark` class — handled in CSS rules.
  const blendClass = autoTransparent ? 've-auto-transparent-logo' : '';

  const inner = isCustom ? (
    <img
      src={branding.logo_url}
      alt={branding?.brand_name || 'Vigilant Entities'}
      className={`relative ${className} object-contain ${blendClass}`}
      style={{ filter: 'none' }}
      draggable={false}
    />
  ) : (
    <DefaultMonogramSVG
      className={`relative ${className}`}
      style={{ filter: LOGO_GLOW }}
    />
  );

  const img = (
    <span className={`relative inline-flex items-center justify-center ${className}`}>
      {/* Optional brand-coloured halo behind the mark when transparent */}
      {autoTransparent && isCustom && (
        <span
          aria-hidden="true"
          className="absolute inset-[-25%] rounded-full blur-2xl opacity-40 ve-logo-halo"
          style={{ background: `radial-gradient(circle, ${primary} 0%, transparent 70%)` }}
        />
      )}
      {inner}
    </span>
  );
  if (noLink) return img;
  return (
    <Link to={to} aria-label="home" data-testid="logo-home-link" className="inline-block hover:opacity-95 transition-opacity">
      {img}
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/*  <LogoFull> — Stacked: big monogram on top, wordmark + subline below.      */
/*  Used in the login splash + hero splashes.                                 */
/* -------------------------------------------------------------------------- */
const FULL_MARK = {
  sm: 'h-20 w-20',
  md: 'h-28 w-28',
  lg: 'h-40 w-40',
  xl: 'h-52 w-52',
  '2xl': 'h-64 w-64',
};
const FULL_WORD = {
  sm: 'text-xl',
  md: 'text-3xl',
  lg: 'text-5xl',
  xl: 'text-6xl',
  '2xl': 'text-7xl',
};

export function LogoFull({ size = 'lg', className = '', noLink = false, to = '/home', showSub = true, showVersion = false }) {
  const dim = FULL_MARK[size] || FULL_MARK.lg;
  const txt = FULL_WORD[size] || FULL_WORD.lg;
  const { branding } = useBranding();
  const customBrand = branding?.brand_name && branding.brand_name !== 'Vigilant Entities';
  const [first, ...rest] = (branding?.brand_name || 'Vigilant Entities').split(' ');
  const second = rest.join(' ');
  const inner = (
    <div className={`flex flex-col items-center gap-4 ${className}`} data-testid="logo-full">
      <LogoMark className={dim} noLink />
      <div className="flex flex-col items-center leading-[1.0]">
        <div className={`${txt} font-serif font-bold tracking-[0.10em] uppercase flex items-baseline gap-2`}>
          {customBrand ? (
            <span style={{ color: 'var(--brand-primary)' }}>{first} {second}</span>
          ) : (
            <>
              <span className="ve-silver-text">Vigilant</span>{' '}
              <span className="ve-violet-ink">Entities</span>
            </>
          )}
          {showVersion && <VersionBadge className="ml-2 self-center" />}
        </div>
        {showSub && !customBrand && (
          <div className="text-[12px] uppercase tracking-[0.30em] mt-3 font-semibold text-[#7932AE] dark:text-[#C77CFF]/85 whitespace-nowrap">
            Holdings <span className="mx-2 opacity-60">•</span> Investments{' '}
            <span className="mx-2 opacity-60">•</span> Incubation
          </div>
        )}
      </div>
    </div>
  );
  if (noLink) return inner;
  return (
    <Link to={to} aria-label="home" data-testid="logo-home-link" className="inline-flex hover:opacity-95 transition-opacity">
      {inner}
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/*  <Logo> — horizontal: monogram on the left, LIVE TEXT wordmark to the      */
/*  right.  Default header / sidebar variant.                                 */
/* -------------------------------------------------------------------------- */
const MARK_SIZE = {
  sm: 'h-10 w-10 sm:h-12 sm:w-12',
  md: 'h-16 w-16',
  lg: 'h-24 w-24',
  xl: 'h-32 w-32',
};
const TEXT_SIZE = {
  sm: 'text-sm sm:text-base',
  md: 'text-2xl',
  lg: 'text-4xl',
  xl: 'text-5xl',
};
const SUB_SIZE = {
  sm: 'text-[8px]',
  md: 'text-[10px]',
  lg: 'text-[12px]',
  xl: 'text-[14px]',
};

export function Logo({
  className = '',
  size = 'md',
  showSub = true,
  noLink = false,
  to = '/home',
  showVersion = false,
}) {
  const markDim = MARK_SIZE[size] || MARK_SIZE.md;
  const textCls = TEXT_SIZE[size] || TEXT_SIZE.md;
  const subCls = SUB_SIZE[size] || SUB_SIZE.md;
  const { branding } = useBranding();
  const customBrand = branding?.brand_name && branding.brand_name !== 'Vigilant Entities';

  const vigilantCls = 've-silver-text font-serif font-bold tracking-[0.10em] uppercase';
  const entitiesCls = 've-violet-ink font-serif font-bold tracking-[0.10em] uppercase';
  const subColor = 'text-[#7932AE] dark:text-[#C77CFF]/85';

  const inner = (
    <div className={`flex items-center gap-3 ${className}`}>
      <LogoMark className={markDim} noLink />
      <div className="flex flex-col leading-[1.0] min-w-0">
        <span className={`${textCls} flex items-baseline gap-2 ${customBrand ? 'font-serif font-bold tracking-[0.10em] uppercase truncate' : vigilantCls}`}
              style={customBrand ? { color: 'var(--brand-primary)' } : undefined}
              title={customBrand ? branding.brand_name : undefined}>
          {customBrand ? branding.brand_name : (<>Vigilant <span className={entitiesCls}>Entities</span></>)}
          {showVersion && (
            <VersionBadge className="ml-1.5 self-center" />
          )}
        </span>
        {showSub && !customBrand && (
          <span
            className={`${subCls} uppercase tracking-[0.30em] mt-1.5 font-semibold whitespace-nowrap ${subColor}`}
          >
            Holdings <span className="mx-1.5 opacity-60">•</span> Investments{' '}
            <span className="mx-1.5 opacity-60">•</span> Incubation
          </span>
        )}
      </div>
    </div>
  );
  if (noLink) return inner;
  return (
    <Link
      to={to}
      aria-label="home"
      data-testid="logo-home-link"
      className="inline-flex hover:opacity-90 transition-opacity"
    >
      {inner}
    </Link>
  );
}

export default Logo;
